import { API_BASE_URL, IS_DEV, featureFlags } from "@/config/env";
import i18n from "@/i18n/i18n";
import { message } from "antd";
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

// 扩展AxiosRequestConfig类型
declare module "axios" {
  interface AxiosRequestConfig {
    metadata?: {
      startTime: number;
      cacheKey?: string;
      cacheTTL?: number;
    };
    // 轻量缓存控制（仅 GET）
    useCache?: boolean;
    cacheTTL?: number; // 毫秒，默认 300000 (5 分钟)
    cacheKey?: string; // 自定义键；默认 url+sorted(params)
  }
}

// 创建axios实例
const http: AxiosInstance = axios.create({
  baseURL: IS_DEV ? "/api" : API_BASE_URL,
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
    "X-Language": i18n.language,
  },
});

const shouldLog = IS_DEV || featureFlags.enableDevtools;
// 全局缓存开关（代码控制）
let globalCacheEnabled = false;
export const setHttpCacheEnabled = (enabled: boolean) => {
  globalCacheEnabled = !!enabled;
};
const defaultTTL = 2 * 60 * 1000;

// 简易响应缓存（仅 GET 且显式开启 useCache）
type CachedEntry = { expireAt: number; response: AxiosResponse };
const responseCache = new Map<string, CachedEntry>();

const buildCacheKey = (config: InternalAxiosRequestConfig): string => {
  if (config.cacheKey) return config.cacheKey;
  const url = config.baseURL
    ? new URL(config.url || "", config.baseURL).toString()
    : config.url || "";
  const params = config.params || {};
  const sortedKeys = Object.keys(params).sort();
  const paramString = sortedKeys
    .map((k) => `${k}=${JSON.stringify(params[k])}`)
    .join("&");
  return `${(config.method || "get").toUpperCase()} ${url}?${paramString}`;
};

// 提供安全的 GET 缓存助手，不修改全局适配器
export async function getWithCache<T = any>(
  url: string,
  config: AxiosRequestConfig & { cacheTTL?: number; cacheKey?: string } = {}
): Promise<AxiosResponse<T>> {
  const method = (config.method || "get").toUpperCase();
  if (method !== "GET")
    return http.request<T>({ ...config, url, method: "GET" });
  const ttl = Number.isFinite(config.cacheTTL as any)
    ? Number(config.cacheTTL)
    : 5 * 60 * 1000;
  const key =
    config.cacheKey ||
    buildCacheKey({
      ...(config as any),
      url,
      method: "GET",
      baseURL: http.defaults.baseURL,
    });

  const cached = responseCache.get(key);
  if (cached && Date.now() < cached.expireAt) {
    if (shouldLog) console.debug("[HTTP Cache] hit:", key);
    return cached.response as AxiosResponse<T>;
  }

  if (shouldLog) console.debug("[HTTP Cache] miss:", key);
  const resp = await http.request<T>({ ...config, url, method: "GET" });
  if (resp && resp.status >= 200 && resp.status < 300) {
    responseCache.set(key, { expireAt: Date.now() + ttl, response: resp });
  }
  return resp;
}

// 请求拦截器
http.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 添加认证token
    const token = localStorage.getItem("token");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 添加请求时间戳
    config.metadata = { startTime: Date.now() };

    // GET 全局缓存（按需启用），仅当未显式禁用 useCache === false
    try {
      const isGet = (config.method || "get").toUpperCase() === "GET";
      const allowCache =
        isGet && config.useCache !== false && globalCacheEnabled;
      if (allowCache) {
        const ttl = Number.isFinite(config.cacheTTL as any)
          ? Number(config.cacheTTL)
          : defaultTTL;
        const key = buildCacheKey(config);
        const cached = responseCache.get(key);
        if (cached && Date.now() < cached.expireAt) {
          if (shouldLog) console.debug("[HTTP Cache][req-hit]:", key);
          // 使用每请求适配器返回缓存响应
          config.adapter = async () => ({ ...cached.response, config }) as any;
        } else {
          // 记录以便响应后写入
          config.metadata = {
            ...(config.metadata || { startTime: Date.now() }),
            cacheKey: key,
            cacheTTL: ttl,
          };
        }
      }
    } catch (e) {
      // ignore cache decision errors
    }

    // 请求日志（path 标红）
    if (shouldLog) {
      const method = (config.method || "get").toUpperCase();
      const url = config.url || "";
      // 使用 console.group 便于折叠查看
      console.groupCollapsed("%cHTTP REQUEST", "color:#999;font-weight:bold");
      console.debug("method:", method);
      console.debug(
        "%cpath:",
        "color:#999",
        `%c${url}`,
        "color:red;font-weight:bold"
      );
      console.debug(
        "%cheaders:",
        "color:#0066cc;font-weight:bold",
        config.headers
      );
      if (config.params) console.debug("params:", config.params);
      if (config.data) console.debug("data:", config.data);
      console.groupEnd();
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
http.interceptors.response.use(
  (response: AxiosResponse) => {
    // 计算请求耗时
    const endTime = Date.now();
    const startTime = response.config.metadata?.startTime;
    if (startTime) {
      if (shouldLog) {
        console.debug(`请求耗时: ${endTime - startTime}ms`);
      }
    }

    // 命中策略：若是 GET 且开启了缓存，则写入缓存
    try {
      const method = (response.config.method || "get").toUpperCase();
      const allowCache =
        method === "GET" &&
        response.config.useCache !== false &&
        globalCacheEnabled;
      const key = response.config.metadata?.cacheKey;
      const ttl = response.config.metadata?.cacheTTL ?? defaultTTL;
      if (allowCache && key) {
        responseCache.set(key, { expireAt: Date.now() + ttl, response });
        if (shouldLog) console.debug("[HTTP Cache][resp-store]:", key);
      }
    } catch (e) {
      // ignore cache decision errors
    }

    // 统一处理响应数据
    const { data } = response;

    // 响应日志（path 标红）
    if (shouldLog) {
      const method = (response.config.method || "get").toUpperCase();
      const url = response.config.url || "";
      console.groupCollapsed("%cHTTP RESPONSE", "color:#999;font-weight:bold");
      console.debug("method:", method, "status:", response.status);
      console.debug(
        "%cpath:",
        "color:#999",
        `%c${url}`,
        "color:red;font-weight:bold"
      );
      console.debug("response:", data);
      console.groupEnd();
    }

    // 检查API响应格式 - 支持标准API响应格式
    if (data && typeof data === "object" && "code" in data) {
      // 标准API响应格式: { code, message, data }
      if (data.code === 200) {
        if (data.data === null) {
          data.data = [];
        }
        return response; // 返回完整响应，让业务层处理data字段
      } else if (data.code === 2001) {
        // 处理登录失效状态码2001
        const errorMessage = data.message || "登录已失效，请重新登录";
        message.error(errorMessage);

        // 清除认证信息并退出登录
        localStorage.removeItem("token");
        localStorage.removeItem("user_id");

        // 异步导入认证store并调用退出方法
        import("@/stores/useAuthStore")
          .then((module) => {
            const { logout } = module.useAuthStore.getState();
            logout();
          })
          .catch((error) => {
            console.error("Failed to import auth store:", error);
            // 即使导入失败，也要确保清除本地存储并跳转
            localStorage.clear();
            window.location.href = "/";
          });

        // 重定向到登录页
        window.location.href = "/";

        return Promise.reject(new Error(errorMessage));
      } else {
        // API业务错误
        const errorMessage = data.message || "请求失败";
        message.error(errorMessage);
        return Promise.reject(new Error(errorMessage));
      }
    } else {
      // 非标准响应格式，直接返回
      return response;
    }
  },
  (error) => {
    // 统一错误处理
    // 首先检查是否为取消错误（用户主动取消请求）
    if (error.code === "ERR_CANCELED" || error.name === "CanceledError") {
      return Promise.reject(error);
    }

    if (error.response) {
      const { status, data } = error.response;
      switch (status) {
        case 401:
          message.error("未授权，请重新登录");
          // 清除token并跳转到登录页
          localStorage.removeItem("token");
          window.location.href = "/login";
          break;
        case 403:
          message.error("拒绝访问");
          break;
        case 404:
          message.error("请求的资源不存在");
          break;
        case 500:
          message.error("服务器内部错误");
          break;
        default:
          message.error(data?.message || "网络错误");
      }
    } else if (error.request) {
      message.error(i18n.t("api.error_network"));
    } else if (error.code === "ECONNABORTED") {
      message.error("请求超时，请稍后重试");
    } else {
      message.error("请求配置错误");
    }

    // 确保错误被正确记录
    console.error("HTTP Request Error:", {
      message: error.message,
      code: error.code,
      config: error.config,
      response: error.response?.data,
    });

    return Promise.reject(error);
  }
);

export default http;
